import React from 'react';
import {
    Box,
    Typography,
    Paper,
    Button,
    Chip,
    Grid,
    IconButton,
    Tooltip,
    Checkbox,
} from '@mui/material';
import {
    Check,
    CloudSync as CloudSyncIcon,
    Edit as EditIcon,
    Schedule,
    Visibility,
    Work,
    Engineering,
    LocationCity,
    CalendarMonth,
    Done,
    Info,
    People,
    Star,
} from '@mui/icons-material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { TimesheetListItemProps } from '../interfaces/timesheet';
import {
    format, startOfWeek,
    addDays
} from 'date-fns';
import { fr } from 'date-fns/locale';

const TimesheetListItem: React.FC<TimesheetListItemProps> = ({
    timesheet,
    onEdit,
    onValidateForeman,
    onValidateHR,
    onDelete,
    userRole,
    isOnline,
    isSelected = false,
    onSelect,
    showCheckbox = false,
    masterData
}) => {
    // Same logic as in TimesheetCard
    const totalHours = timesheet.dayDetails
        ? Object.values(timesheet.dayDetails).reduce((total, dayDetail: any) => {
            const hours = dayDetail.hours || 0;
            const quarters = dayDetail.quarters || 0;
            return total + hours + quarters;
        }, 0)
        : 0;

    const canEdit = userRole && (
        (userRole === 'HR' && !timesheet?.foreman_validated) ||
        (userRole === 'HR' && timesheet?.foreman_validated) ||
        (userRole === 'Foreman' && !timesheet.foreman_validated && !timesheet.hr_validated) ||
        (userRole === 'User' && !timesheet?.foreman_validated)
    );

    const getWorkerMatricule = (): number | undefined => {
        // Find the worker in master data by name
        const worker = timesheet?.travailleur?.name ? timesheet.travailleur.name : '';
        const employee = masterData?.employees?.find(emp =>
            emp.name.toLocaleLowerCase().trim() === worker.toLocaleLowerCase().trim()
        );
        return employee ? employee.matricule : undefined;
    }

    return (
        <Paper
            elevation={1}
            sx={{
                p: 2,
                mb: 2,
                borderLeft: timesheet.foreman_validated && timesheet.hr_validated
                    ? '4px solid #4caf50'
                    : timesheet.foreman_validated && !timesheet.hr_validated
                        ? '4px solid #e0e0e0'
                        : timesheet._pendingUpdate ? '4px solid #ff9800' : timesheet._pendingDelete ? '4px solid #C62828' : !timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.id ? '4px solid #4caf50' : '4px solid #e0e0e0',
            }}
        >
            <Grid container spacing={2} alignItems="center">
                {showCheckbox && timesheet.id && canEdit && (
                    <Grid item xs={1} sm={1} sx={{ display: 'flex', justifyContent: 'center', pl: 1 }}>
                        <Checkbox
                            checked={isSelected}
                            disabled={!isOnline}
                            onChange={() => onSelect && onSelect(timesheet.id as string)}
                        />
                    </Grid>
                )}
                {/* Employee and ID info */}
                <Grid item xs={showCheckbox ? 11 : 12} sm={showCheckbox ? 2 : 2}>
                    <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: "column", flexWrap: 'wrap', gap: 1 }}>
                        {/* tooltip remark */}

                        {timesheet.remark && (
                            <Tooltip title={timesheet.remark} placement="top"
                                enterTouchDelay={0}
                            >
                                <Info fontSize="large" color="warning" sx={{ mr: 1 }} />
                            </Tooltip>
                        )}
                        {/* Timesheet ID */}
                        {(getWorkerMatricule()) && (
                            <Tooltip title={`${(getWorkerMatricule())}`}
                                enterTouchDelay={0}
                            >
                                <Chip
                                    size="small"
                                    label={`${((getWorkerMatricule())?.toString().length || 0) > 10 ?
                                        (getWorkerMatricule())?.toString().substring(0, 5) + '...' :
                                        (getWorkerMatricule())}`}
                                    color={timesheet.id ? "primary" : "error"}
                                    variant="filled"
                                    sx={{ fontSize: '0.75rem' }}
                                />
                            </Tooltip>
                        )}
                        <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                            {timesheet.travailleur.name}
                            {timesheet.travailleur.isInterim && (
                                <Chip
                                    size="small"
                                    label={'Interimaire'}
                                    sx={{ fontSize: '0.75rem', height: '20px', ml: 1, px: 0.5 }}
                                    color="secondary"
                                    icon={<People fontSize="small" />}
                                    variant="outlined"
                                />
                            )}
                        </Typography>
                        {/* Status indicators */}
                        {!timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.id && (
                            <Chip size="small" label="Nouveau" color="success" icon={<CloudSyncIcon fontSize="small" />} />
                        )}
                        {timesheet._pendingUpdate && (
                            <Chip size="small" label="Mis à jour" color="warning" icon={<CloudSyncIcon fontSize="small" />} />
                        )}
                        {timesheet._pendingDelete && (
                            <Chip size="small" label="A supprimer" color="error" icon={<CloudSyncIcon fontSize="small" />} />
                        )}
                    </Box>
                </Grid>

                {/* Main timesheet info */}
                <Grid item xs={showCheckbox ? 11 : 12} sm={showCheckbox ? 3 : 3}>

                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }} component={"div"}>
                                <LocationCity fontSize="small" sx={{ mr: 0.5 }} color="primary" />
                                Chantier: <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'medium' }}>{timesheet.worksite?.name}</Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }} component={"div"}>
                                <Engineering fontSize="small" sx={{ mr: 0.5 }} color="primary" />
                                Conducteur de travaux: <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'medium' }}>{timesheet.chefProjet?.name}</Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Typography variant="body2" color="textSecondary" sx={{ display: 'flex', alignItems: 'center' }} component={"div"}>
                                <CalendarMonth fontSize="small" sx={{ mr: 0.5 }} color="primary" />
                                Semaine: <Typography variant="body2" sx={{ ml: 0.5, fontWeight: 'medium' }}>{timesheet.weekNumber}</Typography>
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Box sx={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                mt: 0.5,
                            }}>
                                {['lundi', 'mardi', 'mercredi', 'jeudi', 'vendredi', 'samedi', 'dimanche'].map((day, index) => {
                                    // Calculate the actual date for this day based on the timesheet date
                                    const baseDate = timesheet.timesheetDate ? new Date(timesheet.timesheetDate) : new Date();
                                    const startOfWeekDate = startOfWeek(baseDate, { locale: fr });
                                    const dayDate = addDays(startOfWeekDate, index);
                                    const formattedDate = format(dayDate, 'dd MMM', { locale: fr });

                                    // Determine color based on the type of entry
                                    let borderColor = 'grey.300';

                                    // Check if this day has bonuses
                                    const dayKey = day as keyof typeof timesheet.dayDetails;
                                    const hasBonuses = timesheet.dayDetails?.[dayKey]?.bonuses &&
                                        timesheet.dayDetails[dayKey]?.bonuses?.length > 0;

                                    // Generate tooltip content
                                    let tooltipContent = (
                                        <Box>
                                            <Typography fontWeight="bold">
                                                {day.charAt(0).toUpperCase() + day.slice(1)} ({formattedDate})
                                                {/* Display worked hours for this day */}
                                                <br></br>
                                                <Typography variant="caption" sx={{ mt: 0.5 }}>
                                                    Heures travaillées: {(timesheet.dayDetails?.[dayKey]?.hours + timesheet.dayDetails?.[dayKey]?.quarters) || 0}h
                                                </Typography>
                                            </Typography>

                                            {hasBonuses && (
                                                <Box>
                                                    <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                                                        Autres codes salaires ({timesheet.dayDetails[dayKey]?.bonuses?.length}):
                                                    </Typography>
                                                    {timesheet.dayDetails[dayKey]?.bonuses?.map((bonus, i) => (
                                                        <Typography variant="body2" key={i} sx={{ ml: 1 }}>
                                                            • {bonus.code}: {bonus.hours}h
                                                        </Typography>
                                                    ))}
                                                </Box>
                                            )}
                                        </Box>
                                    );

                                    return (
                                        <Tooltip
                                            key={day}
                                            title={tooltipContent}
                                            placement="top"
                                            enterTouchDelay={0}
                                            arrow
                                            componentsProps={{
                                                tooltip: {
                                                    sx: {
                                                        bgcolor: 'primary.dark',
                                                        '& .MuiTooltip-arrow': {
                                                            color: 'primary.dark',
                                                        },
                                                        borderRadius: '8px',
                                                        px: 2,
                                                        py: 1,
                                                        boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.15)',
                                                        fontSize: '0.75rem',
                                                        fontWeight: 500,
                                                        maxWidth: '250px'
                                                    }
                                                }
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    width: '32px',
                                                    height: '32px',
                                                    display: 'flex',
                                                    justifyContent: 'center',
                                                    alignItems: 'center',
                                                    borderRadius: '8px',
                                                    border: '1px solid',
                                                    borderColor: hasBonuses ? 'warning.main' : borderColor,
                                                    fontSize: '0.85rem',
                                                    fontWeight: 600,
                                                    color: 'text.primary',
                                                    backgroundColor: hasBonuses ? 'warning.light' : 'background.paper',
                                                    position: 'relative',
                                                    transition: 'all 0.2s ease-in-out',
                                                    boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
                                                        borderColor: 'primary.main',
                                                        color: 'primary.main',
                                                    },
                                                    letterSpacing: '0.5px',
                                                    fontFamily: "Noto Sans, sans-serif"
                                                }}
                                            >
                                                {day.charAt(0).toUpperCase()}
                                                {hasBonuses && (
                                                    <Star
                                                        sx={{
                                                            position: 'absolute',
                                                            top: '-8px',
                                                            right: '-8px',
                                                            fontSize: '14px',
                                                            color: 'warning.main',
                                                            backgroundColor: '#fff',
                                                            borderRadius: '50%',
                                                            padding: '1px'
                                                        }}
                                                    />
                                                )}
                                            </Box>
                                        </Tooltip>
                                    );
                                })}
                            </Box>
                        </Grid>
                    </Grid>
                </Grid>

                {/* Hours info */}
                <Grid item xs={showCheckbox ? 11 : 12} sm={showCheckbox ? 2 : 2}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Chip
                            size="small"
                            icon={<Work fontSize="small" />}
                            label={`${totalHours} Heures travaillées`}
                            color="primary"
                            variant="outlined"
                        />
                    </Box>
                </Grid>

                {/* Validation status */}
                <Grid item xs={showCheckbox ? 11 : 12} sm={showCheckbox ? 1 : 2}>

                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Tooltip title={timesheet.foreman_validated ? "Validée par le conducteur de travaux" : "En attente de validation par le conducteur de travaux"}
                            enterTouchDelay={0}
                        >
                            <Chip
                                size="small"
                                icon={timesheet.foreman_validated ? <Check fontSize="small" /> : <Schedule fontSize="small" />}
                                label={timesheet.foreman_validated ? "Validée par le conducteur de travaux" : "En attente de validation par le conducteur de travaux"}
                                color={timesheet.foreman_validated ? "success" : "warning"}
                                variant="outlined"
                            />
                        </Tooltip>
                        {timesheet.id && !timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.foreman_validated && (userRole?.toLocaleLowerCase() === "Foreman".toLocaleLowerCase() || userRole?.toLocaleLowerCase() === "HR".toLocaleLowerCase()) && (
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<Done />}
                                onClick={() => onValidateForeman && onValidateForeman(timesheet.id as string)}
                                disabled={!isOnline}
                            >
                                Validation du conducteur de travaux
                            </Button>
                        )}

                        <Tooltip title={timesheet.hr_validated ? "Validée par RH" : "En attente de validation apr RH"}
                            enterTouchDelay={0}
                        >
                            <Chip
                                size="small"
                                icon={timesheet.hr_validated ? <Check fontSize="small" /> : <Schedule fontSize="small" />}
                                label={timesheet.hr_validated ? "Validée par RH" : "En attente de validation apr RH"}
                                color={timesheet.hr_validated ? "success" : "warning"}
                                variant="outlined"
                            />
                        </Tooltip>
                        {timesheet.id && !timesheet._pendingDelete && !timesheet._pendingUpdate && !timesheet.hr_validated && userRole?.toLocaleLowerCase() === "HR".toLocaleLowerCase() && (
                            <Button
                                size="small"
                                variant="contained"
                                color="success"
                                onClick={() => onValidateHR && onValidateHR(timesheet.id as string)}
                                disabled={!isOnline}
                            >
                                Validatiuon RH
                            </Button>
                        )}

                    </Box>
                </Grid>

                {/* Actions */}
                <Grid item xs={showCheckbox ? 11 : 12} sm={showCheckbox ? 3 : 3}>
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        {!timesheet._pendingDelete && canEdit && (
                            <Button
                                size="small"
                                onClick={() => onEdit(timesheet)}
                                startIcon={<EditIcon />}
                                variant='contained'
                                color="primary"
                            >
                                Modifier
                            </Button>
                        )}
                        {(!timesheet._pendingDelete) && !canEdit && (
                            <Button
                                size="small"
                                onClick={() => onEdit(timesheet)}
                                startIcon={<Visibility />}
                                variant='contained'
                                color="secondary"
                            >
                                Voir
                            </Button>
                        )}



                        {(!timesheet._pendingDelete && canEdit) && (
                            <IconButton
                                edge="end"
                                color="error"
                                aria-label="delete"
                                onClick={() => onDelete(timesheet)}
                                size="small"
                            >
                                <DeleteIcon />
                            </IconButton>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </Paper>
    );
};

export default TimesheetListItem;